import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import TextInputComponent from '../../../../../components/textinput/TextInputComponent';
import { COLORS, BORDER_RADIUS } from '../../../../../constants/theme';

export default function BankAccountForm({ onSave, onCancel }) {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [iban, setIban] = useState('');

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!accountName.trim()) newErrors.accountName = 'Account Name is required';
    if (!/^\d{8}$/.test(accountNumber)) {
      newErrors.accountNumber = 'Account Number must be exactly 8 digits';
    }
    if (!/^\d{6}$/.test(sortCode)) {
      newErrors.sortCode = 'Sort Code must be exactly 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave({
        accountName,
        accountNumber,
        sortCode,
        swiftCode,
        iban,
      });
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      {/* Modal Header */}
      <View style={styles.modalHeaderRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.modalTitle}>Add Bank Account</Text>
          <Text style={styles.modalSubtitle}>
            Enter your bank details to coordinate payouts and rent allocations.
          </Text>
        </View>
        <TouchableOpacity style={styles.closeIconBtn} onPress={onCancel} activeOpacity={0.7}>
          <Feather name="x" size={18} color="#6B7280" />
        </TouchableOpacity>
      </View>

      {/* Account Name */}
      <View style={styles.fieldGroup}>
        <TextInputComponent
          title="Account Name *"
          placeholder="e.g. SBI Main Rent / Barclays Reserve"
          value={accountName}
          onChangeText={(val) => {
            setAccountName(val);
            if (errors.accountName) setErrors((prev) => ({ ...prev, accountName: '' }));
          }}
          error={errors.accountName}
          height={44}
        />
      </View>

      {/* 2-Column Row: Account Number & Sort Code */}
      <View style={[styles.gridRow, isMobile && styles.gridRowMobile]}>
        <View style={styles.gridCol}>
          <TextInputComponent
            title="Account Number *"
            placeholder="8 digit account number"
            value={accountNumber}
            onChangeText={(text) => {
              const val = text.replace(/[^0-9]/g, '');
              setAccountNumber(val);
              if (errors.accountNumber) setErrors((prev) => ({ ...prev, accountNumber: '' }));
            }}
            keyboardType="numeric"
            maxLength={8}
            error={errors.accountNumber}
            height={44}
          />
        </View>

        <View style={styles.gridCol}>
          <TextInputComponent
            title="Sort Code *"
            placeholder="6 digit sort code"
            value={sortCode}
            onChangeText={(text) => {
              const val = text.replace(/[^0-9]/g, '');
              setSortCode(val);
              if (errors.sortCode) setErrors((prev) => ({ ...prev, sortCode: '' }));
            }}
            keyboardType="numeric"
            maxLength={6}
            error={errors.sortCode}
            height={44}
          />
        </View>
      </View>

      {/* 2-Column Row: SWIFT Code & IBAN */}
      <View style={[styles.gridRow, isMobile && styles.gridRowMobile]}>
        <View style={styles.gridCol}>
          <TextInputComponent
            title="SWIFT Code (Optional)"
            placeholder="Enter SWIFT code"
            value={swiftCode}
            onChangeText={setSwiftCode}
            height={44}
          />
        </View>

        <View style={styles.gridCol}>
          <TextInputComponent
            title="IBAN (Optional)"
            placeholder="Enter IBAN number"
            value={iban}
            onChangeText={setIban}
            height={44}
          />
        </View>
      </View>

      {/* Modal Actions Footer */}
      <View style={styles.footerRow}>
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel} activeOpacity={0.8}>
          <Text style={styles.cancelText}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.saveButton} onPress={handleSubmit} activeOpacity={0.85}>
          <Text style={styles.saveText}>Save Account</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    padding: 24,
    borderRadius: BORDER_RADIUS.lg,
  },

  modalHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#6B7280',
  },
  closeIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },

  fieldGroup: {
    marginBottom: 16,
  },

  gridRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  gridRowMobile: {
    flexDirection: 'column',
    gap: 14,
  },
  gridCol: {
    flex: 1,
  },

  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 10,
    paddingTop: 16,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    height: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  saveButton: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 40,
    paddingHorizontal: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
